import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Axios from "axios";
import { TempFieldConfig, UserFieldConfig } from "./interface";
import moment from "moment";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { SimpleForm } from "@/components/SimpleForm";
import { ConfigCard } from "@/components/ConfigCard";
import { MediaEditForm } from "./mediaEditForm";
import { FormInstance } from "antd/es/form";
import styles from "./styles.module.less";
import { useMediaSize } from "@/hooks/useMediaSize";
import { SaveOutlined } from "@ant-design/icons";
import { Button, Divider, message, Spin, Tooltip } from "antd";
import { useAsyncFn } from "react-use";
import { RightOutlined } from "@ant-design/icons";
import { EditOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router"
import qs from "query-string";

export const UserPageBasic = (props: any) => {
  const location = useLocation();
  const nav = useNavigate();

  const formRef = useRef<FormInstance>(null);
  const [baseFieldList, setBaseFieldList] = useState<TempFieldConfig[]>([]);
  const [customFieldList, setCustomFieldList] = useState<TempFieldConfig[]>([]);
  const [user, setUser] = useState(window.__user__);
  const [editing, setEditing] = useState(qs.parse(location.search)?.["form_status"] === "edit" || false);
  const [loading, setLoadding] = useState(false);
  const { isPhoneMedia } = useMediaSize();
  const [mediaItemKey, setMediaItemKey] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { t } = useTranslation();

  // 表单保存的时候调用重新设置一下 user, 只有基础字段升级的时候才调用 🐕

  // 转换数据到正确的类型，从服务器反馈到表单需要的
  const transDataType = useCallback(
    (item: any) => {
      // console.log("trans", item);
      let dataType: any = "text";
      let rest: any = {};
      switch (item.dataType) {
        case "STRING":
          dataType = "text";
          break;
        case "NUMBER":
          dataType = "number";
          rest.style = {
            width: "100%",
          };
          break;
        case "DATETIME":
          dataType = "datetime";
          rest.format = "YYYY/MM/DD";
          rest.style = {
            width: "100%",
          };
          break;
        case "SELECT":
          dataType = "select";
          rest.options = item.options;
          break;
        case "OBJECT":
          dataType = "select";
          break;
        case "BOOLEAN":
          dataType = "select";
          rest.options = [
            {
              value: "true",
              label: t("common.yes"),
            },
            {
              value: "false",
              label: t("common.no"),
            },
          ];
          break;
        default:
          dataType = "text";
      }
      return {
        type: dataType,
        dataType: item.dataType,
        unRender: !item.accessControl,
        label: item.label,
        name: item.key,
        ...rest,
      };
    },
    [t]
  );
  const genderOptions = useCallback(
    (key: string) => {
      const options = [
        { label: t("common.unknow"), value: "U" },
        { label: t("common.man"), value: "M" },
        { label: t("common.female"), value: "W" },
      ];
      const findOption = options.find((item) => item.value === key);
      if (findOption) {
        return findOption.label;
      } else {
        return "-";
      }
    },
    [t]
  );
  // 查询 udf 权限和用户基础字段
  const fetchUdfPowerAndBasicFields = useCallback(async () => {
    const { data } = await Axios.post("/api/v2/udfs/all", {
      targetType: "USER",
    });

    const fieldConfig: UserFieldConfig[] = data;
    const baseFieldConfig = _.filter(fieldConfig, (x) => x.isBase);
    const visibleBaseFields = baseFieldConfig
      .filter((x) => x.userVisible)
      .map((x) => x.key);
    const totalUserFields: TempFieldConfig[] = [
      {
        type: "text",
        label: "ID",
        name: "id",
        value: user?.id,
        unRender: true,
      },
      {
        type: "text",
        label: t("common.username"),
        name: "username",
        value: user?.username,
      },
      {
        type: "text",
        label: t("common.email"),
        name: "email",
        value: user?.email,
        rules: [
          {
            type: "email",
            message: t("common.emailFormatError"),
          },
        ],
        unRender: true,
      },
      {
        type: "text",
        label: t("common.phoneNumber"),
        name: "phone",
        value: user?.phone,
        rules: [
          {
            pattern: /^\d*$/,
            message: t("common.phoneFormateError"),
          },
        ],
        unRender: true,
      },
      {
        type: "text",
        label: t("user.name"),
        name: "name",
        value: user?.name,
      },
      {
        type: "text",
        label: t("common.nickname"),
        name: "nickname",
        value: user?.nickname,
      },
      {
        type: "text",
        label: "Family Name",
        name: "familyName",
        value: user?.familyName,
      },
      {
        type: "text",
        label: "Given Name",
        name: "givenName",
        value: user?.givenName,
      },
      {
        type: "text",
        label: "Middle Name",
        name: "middleName",
        value: user?.middleName,
      },
      {
        type: "text",
        label: "Preferred Username",
        name: "preferredUsername",
        value: user?.preferredUsername,
      },
      {
        type: "text",
        label: t("user.profile"),
        name: "profile",
        value: user?.profile,
      },
      {
        type: "text",
        name: "zoneinfo",
        label: t("user.zoneinfo"),
        value: user?.zoneinfo,
      },
      {
        type: "text",
        name: "website",
        label: t("user.website"),
        value: user?.website,
      },
      {
        type: "text",
        name: "country",
        label: t("user.country"),
        value: user?.country,
      },
      {
        type: "text",
        name: "province",
        label: t("user.province"),
        value: user?.province,
      },
      {
        type: "text",
        name: "city",
        label: t("user.city"),
        value: user?.city,
      },
      {
        type: "text",
        name: "address",
        label: t("user.address"),
        value: user?.address,
      },
      {
        type: "text",
        name: "streetAddress",
        label: t("user.streetAddress"),
        value: user?.streetAddress,
      },
      {
        type: "text",
        name: "externalId",
        label: t("user.externalId"),
        value: user?.externalId,
      },
      {
        type: "text",
        name: "postalCode",
        label: t("user.postalCode"),
        value: user?.postalCode,
      },
      {
        type: "text",
        name: "formatted",
        label: t("user.formatted"),
        value: user?.formatted,
      },
      {
        type: "select",
        label: t("user.gender"),
        name: "gender",
        value: user?.gender,
        options: [
          { label: t("common.unknow"), value: "U" },
          { label: t("common.man"), value: "M" },
          { label: t("common.female"), value: "W" },
        ],
        customComponent: genderOptions(user?.gender as string),
      },
      {
        type: "datetime",
        label: t("user.birthday"),
        name: "birthdate",
        value: user?.birthdate,
        format: "YYYY/MM/DD",
        style: {
          width: "100%",
        },
        customComponent: moment(user?.birthdate).format("YYYY/MM/DD"),
      },
      {
        type: "text",
        label: t("common.company"),
        name: "company",
        value: user?.company,
      },
      {
        type: "datetime",
        label: t("common.registerTime"),
        name: "signedUp",
        value: user?.signedUp,
        format: "YYYY/MM/DD",
        style: {
          width: "100%",
        },
        customComponent: moment(user?.signedUp).format("YYYY/MM/DD"),
        unRender: true,
      },
    ];
    const baseFieldList: TempFieldConfig[] = totalUserFields
      .filter((item) => {
        return visibleBaseFields.includes(item.name as string);
      })
      .map((item) => {
        const settingsFromServer = _.find(
          fieldConfig,
          (x) => x.key === item.name
        );
        const mustNotEditable = ["phone", "email", "signedUp", "id"];
        if (mustNotEditable.includes(item.name as string)) {
          item.unRender = true;
        } else {
          // console.log(settingsFromServer);
          item.unRender = !settingsFromServer?.accessControl;
        }
        // 判断 默认字段的 label 是否编辑过
        if (settingsFromServer) {
          if (Object.keys(settingsFromServer).includes("updatedAt")) {
            // 被编辑过
            item.label = settingsFromServer.label;
          }
        }
        return item;
      });
    setBaseFieldList(baseFieldList);

    const customFieldConfig = _.filter(fieldConfig, (x) => !x.isBase).filter(
      (x) => x.userVisible
    );
    const customFieldList: TempFieldConfig[] = customFieldConfig.map((item) => {
      return transDataType(item);
    });
    // setCustomFieldList(customFieldList);
    return customFieldList;
  }, [genderOptions, t, transDataType, user]);

  // 查询具体的 udv
  const fetchUdv = useCallback(async () => {
    return await Axios.post(`/api/v2/udvs/get`, {
      targetType: "USER",
      targetId: user?.id,
    });
  }, [user]);

  const fetchData = useCallback(async () => {
    return await Promise.all([fetchUdfPowerAndBasicFields(), fetchUdv()]).then(
      (results) => {
        const powerList = results[0] as any;
        const valueList = results[1] as any;
        // 把第二个 list 的 value 加到 第一个上面
        setCustomFieldList(
          powerList.map((item: any) => {
            // 在第二个列表中找 key
            const r = valueList.filter((each: any) => each.key === item.name);
            if (r && r.length > 0) {
              // 找到了,
              if (item.type === "datetime") {
                const value = r[0].value;
                item.value = moment(value).toISOString();
                item.customComponent = item.value
                  .split("T")[0]
                  .split("-")
                  .join("/");
              } else if (r[0].dataType === "SELECT") {
                item.customComponent = item.options.find((i: any) => {
                  return i.value === r[0].value;
                })?.label;
                item.value = r[0].value;
              } else if (r[0].dataType === "BOOLEAN") {
                item.value = r[0].value ? "true" : "false";
                item.customComponent = r[0].value
                  ? t("common.yes")
                  : t("common.no");
              } else {
                const result = r[0];
                item.value = result.value;
              }
              return item;
            } else {
              return item;
            }
          })
        );
        return;
      }
    );
  }, [fetchUdfPowerAndBasicFields, fetchUdv, setCustomFieldList, t]);

  const fieldConfig = useMemo(() => {
    return [...baseFieldList, ...customFieldList];
  }, [baseFieldList, customFieldList]);
  const [saveGroup, saveGroupFn] = useAsyncFn(
    async (data: any) => {
      setLoadding(true);
      // 判断拆分用户基础字段和拓展字段。
      const baseKeys = baseFieldList
        .filter((i) => !i.unRender)
        .map((i) => i.name);
      const customKeys = customFieldList
        .filter((i) => !i.unRender)
        .map((i) => i.name);
      // 提取出用户基础字段需要更新的
      let resParams: { [key: string]: string } = {};
      baseKeys.forEach((i) => {
        if (data![i as never] !== window.__user__![i as never]) {
          resParams[i as string] = data![i as never];
        }
      });
      const customFieldsToUpdate = {} as any;
      // 查找拓展字段需要更新的
      customKeys.forEach((key: any) => {
        // 先判断是否实际修改了
        const currFieldData = fieldConfig.find((i) => i.name === key);
        if (currFieldData && currFieldData.value !== data[key as string]) {
          // 实际修改了，根据类型生成接口发送的数据
          if (
            data[key] === "" ||
            data[key] === undefined ||
            data[key] === null
          ) {
            // 什么都不做
          } else if (currFieldData.dataType === "BOOLEAN") {
            customFieldsToUpdate[key] = data[key] === "true" ? true : false;
          } else if (data[key] instanceof moment) {
            customFieldsToUpdate[key] = data[key].unix() * 1000;
          } else {
            customFieldsToUpdate[key] = data[key];
          }
        }
      });
      try {
        // if (Object.keys(resParams).length > 0) {
        const resUser = await window.__authing__.updateProfile({
          ...resParams,
        });
        // }
        if (Object.keys(customFieldsToUpdate).length > 0) {
          await window.__authing__.setUdfValue(customFieldsToUpdate);
        }
        window.__user__ = resUser;
        setUser(resUser);
        message.success(t("common.updateSuccess"));
      } catch (err) {
        throw err;
      } finally {
        setEditing(false);
        setLoadding(false);

      }
    },
    [user, fieldConfig, setLoadding]
  );
  const cardAction = editing ? (
    <>
      <Button type="link" onClick={() => {
        setEditing(false)
        const search = qs.parse(location.search)
        delete search["form_status"]
        nav(`/u?${qs.stringify(search)}`)

      }}>
        {t("common.cancel")}
      </Button>
      <Divider type="vertical" />
      <Button
        loading={saveGroup.loading}
        type="link"
        onClick={() => {
          saveGroupFn(formRef.current?.getFieldsValue());
        }}
        icon={<SaveOutlined />}
      >
        {t("common.save")}
      </Button>
    </>
  ) : (
    <Button
      onClick={() => {
        setEditing(true);
      }}
      type="link"
      icon={<EditOutlined />}
    >
      {t("common.edit")}
    </Button>
  );
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  useEffect(() => {
    // 把 fieldConfig 变成表格应该有的形式，并设置到表格里。
    const toSet = {} as any;
    fieldConfig.forEach((i) => {
      if (i?.type === "datetime") {
        // 判断日期类型
        toSet[i.name as string] = i.value && moment(i.value);
      } else {
        toSet[i.name as string] = i.value;
      }
    });
    formRef.current!.setFieldsValue(toSet);
  }, [fieldConfig, mediaItemKey, formRef]);
  const editItem = useCallback<
    (item: TempFieldConfig, index: number) => TempFieldConfig
  >((item, index) => {
    const editItem = {
      ...item,
      key: `${index}-${item.name}`,
      fieldCol: item.fieldCol || {
        span: 12,
      },
    };
    return editItem;
  }, []);
  const mediaUnEditItem = useCallback(
    (item, index) => ({
      type: "custom",
      key: `${item.name}-${index}`,
      customComponent: (
        <div
          className={styles.inputSpan}
          onClick={() => {
            if (!item.unRender) {
              setMediaItemKey(item.name);
              setIsModalVisible(true);
            }
          }}
        >
          <div className={styles.inputSpanHeader}>{item.label}</div>
          <div className={styles.inputSpanFooter}>
            {item.value ? item.customComponent || item.value : ""}
            {item.unRender || <RightOutlined />}
          </div>
        </div>
      ),
    }),
    []
  );
  const mediaEditItem = useCallback(
    (item, index) =>
      item.unReder
        ? null
        : {
          ...item,
          key: `${index}-${item.name}`,
          fieldCol: item.fieldCol || {
            span: 24,
          },
        },
    []
  );

  const unEditItemToolTip = useCallback((item: TempFieldConfig) => {
    if (!item.value) {
      return (
        <div className={styles.inputSpan}>
          {"-"}
        </div>
      )
    }
    if (item.value && item.customComponent) {
      return (
        <div className={styles.inputSpan}>
          {item.customComponent}
        </div>
      )
    }
    // 根据长度算一下需不需要 tooltip
    const value = item.value as string;
    // 长度大于 30 才需要 tooltip
    const numberAndEnglish = /[a-zA-Z0-9]+/;
    let len = 0;
    for (const c of value) {
      if (numberAndEnglish.test(c)) {
        len += 1;
      } else {
        len += 2;
      }
    }
    if (len > 30) {
      return (
        <Tooltip title={item?.value}>
          <div className={styles.inputSpan}>
            {item?.value}
          </div>
        </Tooltip>
      )
    } else {
      return (<div className={styles.inputSpan}>
        {item?.value}
      </div>)
    }
  }, [])

  const unEditItem = useCallback<
    (item: TempFieldConfig, index: number) => TempFieldConfig
  >(
    (item: TempFieldConfig, index: number) => ({
      type: "custom",
      label: item.label,
      key: `${item.name}-${index}`,
      customComponent: unEditItemToolTip(item),
      fieldCol: item.fieldCol || {
        span: 12,
      },
    }),
    []
  );
  const mediaConfig = useMemo(
    () => fieldConfig.map((item, index) => mediaUnEditItem(item, index)),
    [fieldConfig, mediaUnEditItem]
  );
  const defConfig = useMemo(
    () =>
      editing
        ? fieldConfig.map((item, index) =>
          item.unRender ? unEditItem(item, index) : editItem(item, index)
        )
        : fieldConfig.map((item, index) => unEditItem(item, index)),
    [editItem, editing, fieldConfig, unEditItem]
  );
  // 计算各种卡片和配置
  const useFieldConfig = useMemo(
    () => (isPhoneMedia ? mediaConfig : defConfig),
    [defConfig, isPhoneMedia, mediaConfig]
  );
  // 计算渲染
  const mediaEditFormReder = useMemo(
    () =>
      fieldConfig
        .filter((item) => item.name === mediaItemKey)
        ?.map((item, index) => mediaEditItem(item, index)),
    [fieldConfig, mediaEditItem, mediaItemKey]
  );
  return (
    <div>
      <ConfigCard
        title={t("common.baseInfo")}
        extra={isPhoneMedia || cardAction}
        className={styles.basic}
      >
        <Spin spinning={loading}>
          <SimpleForm
            ref={formRef}
            labelAlign="left"
            layout="vertical"
            labelCol={{ span: 24 }}
            useRowLayout={true}
            rowProps={{
              gutter: 36,
            }}
            fieldConfig={useFieldConfig as TempFieldConfig[]}
          />
          <MediaEditForm
            user={user!}
            isModalVisible={isModalVisible}
            handleOkCb={(value: any) => {
              saveGroupFn(value);
              setIsModalVisible(false);
            }}
            handleCancelCb={() => setIsModalVisible(false)}
            renderFieldConfig={mediaEditFormReder as TempFieldConfig[]}
          />
        </Spin>
      </ConfigCard>
    </div>
  );
};
